import React, {
    FC, useCallback, useEffect, useState
} from 'react'
import { Button, Form } from 'react-bootstrap'
import styled from 'styled-components'

import { _ } from 'utils'
import { $white, $gray_300 } from 'utils/colors'
import { EditorMode } from '../types'

interface FormControlWrapperProps {
    tapToEdit?: boolean
}

export interface EditableTextFieldProps {
    defaultValue: string
    inputType: 'text' | 'textarea'
    label: string
    onUpdate(value: string, setValue?: React.Dispatch<React.SetStateAction<string>>): void
    tapToEdit?: boolean
}

const S = {
    FormLabel: styled(Form.Label)`
        font-weight: 700;
        padding: 0 0 0 10px;
        color: rgba(78, 81, 85, 0.8);
        margin-left: -10px;
    `,

    // NOTE: A wrapper created because it was difficult to style the component AND
    // set the input type with 'as' or 'type'.
    FormControlWrapper: styled.div(
        ({ tapToEdit = true }: FormControlWrapperProps) => `
        margin-left: ${tapToEdit ? '-10px' : '0'};

        .form-control {
            border:  1px solid ${tapToEdit ? $white : $gray_300} !important;
            font-size: 14px;
            line-height: 16px;
            padding: 10px 9px;
            resize: none;
            border-radius: 4px;
            transition: background-color 250ms ease-in-out;

            &:disabled,
            &[readonly] {
                background-color: ${$white};
            }

            &:hover {
                background-color: ${
    tapToEdit ? 'rgba(45, 48, 53, 0.03)' : $white
};
                cursor: ${tapToEdit ? 'pointer' : 'default'};
            }

            &:focus {
                box-shadow: none;
                background: rgba(255, 255, 255, 0.03);
                border: 1px solid rgba(24, 28, 33, 0.05) !important;
            }
        }

        input[type='text'].form-control {
            font-weight: 400;
        }

        textarea.form-control {
            font-weight: 300;
        }
    `
    ),
}

const EditableTextField: FC<EditableTextFieldProps> = ({
    defaultValue,
    inputType,
    label,
    onUpdate,
    tapToEdit = true,
}: EditableTextFieldProps) => {
    const [value, setValue] = useState(defaultValue || '')
    const [editMode, setEditMode] = useState(
        tapToEdit ? EditorMode.preview : EditorMode.write
    )
    const [saveDisabled, setSaveDisabled] = useState(defaultValue === '')

    const inputClickHandler = useCallback(() => {
        setEditMode(EditorMode.write)
    }, [setEditMode])

    const inputChangeHandler = useCallback(
        (e) => {
            setValue(e.currentTarget.value)

            if (tapToEdit) {
                setSaveDisabled(e.currentTarget.value === '')
            } else {
                onUpdate(e.currentTarget.value)
            }
        },
        [onUpdate, setSaveDisabled, setValue, tapToEdit]
    )

    const buttonUpdateClickHandler = useCallback(() => {
        if (onUpdate) {
            onUpdate(value, setValue)
        }
        setEditMode(EditorMode.preview)
    }, [onUpdate, setEditMode, value])

    const buttonCancelClickHandler = useCallback(() => {
        setValue(defaultValue)
        setEditMode(EditorMode.preview)
    }, [defaultValue, setValue, setEditMode])

    const controlProps = {
        value,
        onChange: inputChangeHandler,
        onClick: inputClickHandler,
        readOnly: editMode === EditorMode.preview,
    }

    useEffect(() => {
        setValue(defaultValue)
    }, [defaultValue])

    return (
        <Form.Group>
            <S.FormLabel>{label}</S.FormLabel>
            <S.FormControlWrapper tapToEdit={tapToEdit}>
                {inputType === 'textarea' ? (
                    <Form.Control as={inputType} {...controlProps} />
                ) : (
                    <Form.Control type={inputType} {...controlProps} />
                )}
            </S.FormControlWrapper>
            {tapToEdit && editMode === EditorMode.write && (
                <>
                    <Button
                        variant="primary mt-2 mr-2"
                        onClick={buttonUpdateClickHandler}
                        disabled={saveDisabled}
                    >
                        {_('form.save')}
                    </Button>
                    <Button
                        variant="default mt-2"
                        onClick={buttonCancelClickHandler}
                    >
                        {_('form.cancel')}
                    </Button>
                </>
            )}
        </Form.Group>
    )
}

export default EditableTextField
